import { makeAutoObservable, runInAction } from 'mobx';
import { FeedItem } from '@/types/feed';
import { api } from '@/services/api';
import { RootStore } from './index';


/**
 * Store for managing feed content in Vietrro
 */
export default class FeedStore {
    rootStore: RootStore;
    feedItems: FeedItem[] = [];
    isLoading = false;
    isLoadingMore = false;
    hasMore = true;
    error: string | null = null;
    page = 1;
    pageSize = 10;
    unreadCount = 0;

    // Feed distribution settings
    distribution = {
        alerts: 40,
        updates: 30,
        insights: 20,
        community: 10
    };

    constructor(rootStore: RootStore) {
        makeAutoObservable(this, { rootStore: false });
        this.rootStore = rootStore;
    }

    /**
     * Load initial feed items
     */
    async loadFeed() {
        this.isLoading = true;
        this.error = null;
        this.page = 1;

        try {
            // In a real application, this would call an API
            // const response = await api.get('/feed', { params: { page: 1, limit: this.pageSize } });
            // For demo purposes, we'll use mock data
            const response = await this.getMockFeed();

            runInAction(() => {
                this.feedItems = response.data;
                this.hasMore = response.hasMore;
                this.countUnread();
            });
        } catch (error) {
            runInAction(() => {
                this.error = 'Failed to load feed';
                console.error('Error loading feed:', error);
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    /**
     * Load more feed items (pagination)
     */
    async loadMoreItems() {
        if (!this.hasMore || this.isLoading || this.isLoadingMore) {
            return;
        }

        this.isLoadingMore = true;
        this.page += 1;

        try {
            // In a real application, this would call an API
            // const response = await api.get('/feed', { 
            //   params: { page: this.page, limit: this.pageSize } 
            // });

            // For demo purposes, we'll use mock data with a delay
            await new Promise(resolve => setTimeout(resolve, 1000));
            const response = await this.getMockFeed(this.page);

            runInAction(() => {
                this.feedItems = [...this.feedItems, ...response.data];
                this.hasMore = response.hasMore;
            });
        } catch (error) {
            runInAction(() => {
                this.error = 'Failed to load more items';
                console.error('Error loading more items:', error);
            });
        } finally {
            runInAction(() => {
                this.isLoadingMore = false;
            });
        }
    }

    /**
     * Refresh feed with latest data
     */
    async refreshFeed() {
        return this.loadFeed();
    }

    /**
     * Check for new feed items
     */
    async checkForNewItems() {
        try {
            // In a real app, this would call an API to check for new items
            // const response = await api.get('/feed/new-items-count');
            // const newItemsCount = response.data.count;

            // For demo purposes, randomly decide if there are new items
            const hasNewItems = Math.random() > 0.7;

            if (hasNewItems) {
                // In a real app, you'd either load the new items or update a counter
                console.log('New feed items available');
                // We could show a notification or update a badge
            }
        } catch (error) {
            console.error('Error checking for new items:', error);
        }
    }

    /**
     * Mark a feed item as read
     */
    markAsRead(id: string) {
        const item = this.feedItems.find(item => item.id === id);
        if (item && !item.read) {
            item.read = true;
            this.countUnread();

            // In a real app, call API to update read status
            // api.put(`/feed/items/${id}/read`);
        }
    }

    /**
     * Mark all feed items as read
     */
    markAllAsRead() {
        this.feedItems.forEach(item => {
            item.read = true;
        });
        this.unreadCount = 0;

        // In a real app, call API to update all items
        // api.put('/feed/items/read-all');
    }

    /**
     * Count unread feed items
     */
    countUnread() {
        this.unreadCount = this.feedItems.filter(item => !item.read).length;
    }

    /**
     * View details of a feed item
     */
    viewDetails(id: string) {
        this.markAsRead(id);
        // In a real app, navigate to detailed view or open modal
        console.log(`View details for item ${id}`);
    }

    /**
     * Take action on a feed item
     */
    takeAction(id: string) {
        this.markAsRead(id);
        // In a real app, navigate to action page or open modal
        console.log(`Take action for item ${id}`);
    }

    /**
     * Save a feed item for later
     */
    saveItem(id: string) {
        const item = this.feedItems.find(item => item.id === id);
        if (item) {
            item.saved = !item.saved;

            // In a real app, call API to update saved status
            // api.put(`/feed/items/${id}/save`, { saved: item.saved });
        }
    }

    /**
     * Set distribution preferences for feed content types
     */
    setDistribution(type: string, value: number) {
        if (type in this.distribution) {
            this.distribution[type as keyof typeof this.distribution] = value;

            // In a real app, save preferences to user settings
            // api.put('/users/preferences/feed', { distribution: this.distribution });

            // Refresh feed with new distribution
            this.loadFeed();
        }
    }

    /**
     * Get mock feed data for demo purposes
     */
    private async getMockFeed(page = 1): Promise<{ data: FeedItem[], hasMore: boolean }> {
        // This simulates API data for demonstration
        const mockFeedItems: FeedItem[] = [
            {
                id: '1',
                type: 'alert',
                title: 'Phát hiện vi phạm bản quyền trên website X',
                content: 'Hệ thống AI của chúng tôi đã phát hiện tác phẩm "Bộ ảnh Hà Nội mùa thu" đang được sử dụng trái phép trên website thương mại example.com.',
                timestamp: '2 giờ trước',
                source: 'Hệ thống giám sát',
                read: false,
                priority: 'high',
                metadata: {
                    contentId: '12345',
                    violationUrl: 'https://example.com/unauthorized-page'
                }
            },
            {
                id: '2',
                type: 'update',
                title: 'Doanh thu tháng 3 tăng 15% so với tháng 2',
                content: 'Tác phẩm của bạn đã tạo ra 5.2 triệu VND doanh thu trong tháng 3/2023, tăng 15% so với tháng trước. Nhấn vào đây để xem báo cáo chi tiết.',
                timestamp: '3 giờ trước',
                source: 'Báo cáo tài chính',
                read: false,
                priority: 'medium',
                metadata: {
                    reportId: '2023-03',
                    revenue: 5200000
                }
            },
            {
                id: '3',
                type: 'alert',
                title: 'Giấy phép cho tác phẩm Y sắp hết hạn (7 ngày)',
                content: 'Giấy phép số LIC-002 cho tác phẩm "Video du lịch Sapa" sẽ hết hạn sau 7 ngày. Vui lòng gia hạn để tiếp tục bảo vệ tác phẩm của bạn.',
                timestamp: '5 giờ trước',
                source: 'Quản lý giấy phép',
                read: false,
                priority: 'medium',
                metadata: {
                    licenseId: 'LIC-002',
                    expiryDate: '2023-04-10'
                }
            },
            {
                id: '4',
                type: 'insight',
                title: 'Xu hướng mới: Nhu cầu nội dung Z tăng 40%',
                content: 'Phân tích của chúng tôi cho thấy nhu cầu về nội dung trong lĩnh vực của bạn đã tăng 40% trong quý này. Đây là thời điểm tốt để tạo thêm nội dung mới.',
                timestamp: '9 giờ trước',
                source: 'Phân tích thị trường',
                read: true,
                priority: 'low',
                metadata: {
                    category: 'market-trends',
                    relevance: 0.85
                }
            },
            {
                id: '5',
                type: 'community',
                title: 'Webinar "Bảo vệ nội dung số" vào 15/04',
                content: 'Vietrro tổ chức webinar về "Chiến lược bảo vệ nội dung số hiệu quả" vào ngày 15/04/2023. Đăng ký ngay để nhận thông tin.',
                timestamp: 'Hôm qua',
                source: 'Sự kiện Vietrro',
                read: true,
                priority: 'low',
                metadata: {
                    eventDate: '2023-04-15T14:00:00',
                    registrationUrl: 'https://vietrro.com/events/digital-content-protection'
                }
            },
            {
                id: '6',
                type: 'alert',
                title: 'Phát hiện deepfake sử dụng hình ảnh của bạn',
                content: 'Hệ thống AI đã phát hiện video deepfake đang sử dụng hình ảnh của bạn trên nền tảng TikTok. Cần xem xét và có hành động ngay.',
                timestamp: 'Hôm qua',
                source: 'AI Protection',
                read: true,
                priority: 'high',
                metadata: {
                    contentId: '67890',
                    platform: 'tiktok',
                    videoUrl: 'https://tiktok.com/example/deepfake-video'
                }
            }
        ];

        // For page 2 and beyond, generate more items
        if (page > 1) {
            const additionalItems: FeedItem[] = [
                {
                    id: `7-${page}`,
                    type: 'update',
                    title: 'Yêu cầu cấp phép mới cho tác phẩm của bạn',
                    content: 'Công ty ABC đã gửi yêu cầu sử dụng tác phẩm "Bộ ảnh Hà Nội mùa thu" cho mục đích quảng cáo thương mại.',
                    timestamp: '2 ngày trước',
                    source: 'Quản lý giấy phép',
                    read: true,
                    priority: 'medium',
                    metadata: {
                        requestId: 'REQ-456',
                        companyName: 'Công ty ABC'
                    }
                },
                {
                    id: `8-${page}`,
                    type: 'insight',
                    title: 'Gợi ý bảo vệ: Thêm watermark kỹ thuật số',
                    content: 'Phân tích cho thấy việc thêm watermark kỹ thuật số có thể giảm 40% vi phạm cho loại nội dung hình ảnh của bạn.',
                    timestamp: '2 ngày trước',
                    source: 'Protection Advisor',
                    read: true,
                    priority: 'low',
                    metadata: {
                        protectionScore: 85,
                        recommendationType: 'watermark'
                    }
                },
                {
                    id: `9-${page}`,
                    type: 'community',
                    title: 'Câu chuyện thành công: Tác giả Z tăng 200% doanh thu',
                    content: 'Tác giả Z đã tăng 200% doanh thu sau khi tối ưu hóa chiến lược bản quyền và áp dụng các công cụ bảo vệ của Vietrro.',
                    timestamp: '3 ngày trước',
                    source: 'Vietrro Blog',
                    read: false,
                    priority: 'low',
                    metadata: {
                        authorId: 'user-789',
                        blogUrl: 'https://vietrro.com/blog/success-story-z'
                    }
                }
            ];

            return {
                data: additionalItems,
                hasMore: page < 4 // Limit to 4 pages for demo
            };
        }

        return {
            data: mockFeedItems,
            hasMore: true
        };
    }
}